import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/apiClient";
import {
  Star, BadgeCheck, MapPin, Image as ImageIcon,
  Sprout, ChevronDown, ChevronUp, Link2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import ImageUploader from "./ImageUploader";

export function StarRow({ value = 0, size = 14, onClick }) {
  return (
    <span className="inline-flex">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          fill={n <= Math.round(value) ? "var(--secondary)" : "transparent"}
          stroke={n <= Math.round(value) ? "var(--secondary)" : "var(--text-muted)"}
          style={{ width: size, height: size, cursor: onClick ? "pointer" : "default" }}
          onClick={onClick ? () => onClick(n) : undefined}
        />
      ))}
    </span>
  );
}

// v3.99ah — Phase 1 P1: structured 14-field strain review schema. Tight
// enum vocabulary mirrors the backend ReviewIn model so the UI and
// validation stay in lockstep.
const STRETCH_OPTIONS       = ["minimal", "moderate", "heavy"];
const YIELD_CLASS_OPTIONS   = ["low", "moderate", "high", "exceptional"];
const HERM_OPTIONS          = ["none", "stress_only", "occasional", "frequent"];
const ENV_SENS_OPTIONS      = ["low", "moderate", "high"];
const DIFFICULTY_OPTIONS    = ["beginner", "intermediate", "advanced", "expert"];

const ENUM_LABEL = {
  minimal: "Minimal", moderate: "Moderate", heavy: "Heavy",
  low: "Low", high: "High", exceptional: "Exceptional",
  none: "None", stress_only: "Stress-only", occasional: "Occasional", frequent: "Frequent",
  beginner: "Beginner", intermediate: "Intermediate", advanced: "Advanced", expert: "Expert",
};

const STRUCTURED_SCORE_FIELDS = [
  { key: "germination_success",  label: "Germination success" },
  { key: "vigor",                label: "Vigor" },
  { key: "potency_perception",   label: "Potency" },
  { key: "wash_hash_quality",    label: "Wash / hash quality" },
  { key: "breeder_accuracy",     label: "Breeder accuracy" },
  { key: "overall_satisfaction", label: "Overall satisfaction" },
];

const STRUCTURED_ENUM_FIELDS = [
  { key: "stretch",                   label: "Stretch",          options: STRETCH_OPTIONS },
  { key: "yield_class",               label: "Yield class",      options: YIELD_CLASS_OPTIONS },
  { key: "herm_tendency",             label: "Herm tendency",    options: HERM_OPTIONS },
  { key: "environmental_sensitivity", label: "Env. sensitivity", options: ENV_SENS_OPTIONS },
  { key: "difficulty_level",          label: "Difficulty",       options: DIFFICULTY_OPTIONS },
];

function emptyStructured() {
  return {
    germination_success: null, vigor: null, flowering_time_days: "",
    stretch: "", terpene_profile: "", potency_perception: null,
    yield_class: "", wash_hash_quality: null, herm_tendency: "",
    environmental_sensitivity: "", difficulty_level: "", smoke_effect: "",
    breeder_accuracy: null, overall_satisfaction: null,
  };
}

// Compact stepper for 1–5 scores. Premium-archival look: filled square = set.
function ScoreStepper({ value, onChange, testId }) {
  return (
    <div className="inline-flex items-center gap-1" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((n) => {
        const active = value === n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(active ? null : n)}
            className="w-6 h-6 border text-[11px] font-mono transition-colors"
            style={{
              borderColor: active ? "var(--gold)" : "var(--border)",
              background: active ? "var(--gold)" : "transparent",
              color: active ? "#1a1a1a" : "var(--text-muted)",
            }}
            data-testid={`${testId}-${n}`}
            aria-label={`${n} of 5`}
          >
            {n}
          </button>
        );
      })}
      {value != null && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-[10px] font-mono uppercase tracking-[0.18em] ml-1"
          style={{ color: "var(--text-muted)" }}
          data-testid={`${testId}-clear`}
        >
          clear
        </button>
      )}
    </div>
  );
}

// v3.99ah — Display block: render only the structured fields that were
// actually filled in. Archival/Bloomberg-style 2-col grid of label →
// value rows. No emoji, no chrome, no progress bars.
function StructuredReviewDisplay({ structured, reviewId }) {
  if (!structured) return null;
  const rows = [];
  STRUCTURED_SCORE_FIELDS.forEach((f) => {
    const v = structured[f.key];
    if (v != null && v >= 1 && v <= 5) {
      rows.push({ key: f.key, label: f.label, value: `${v}/5`, kind: "score" });
    }
  });
  const flowering = structured.flowering_time_days;
  if (typeof flowering === "number" && flowering > 0) {
    rows.push({ key: "flowering_time_days", label: "Flowering time", value: `${flowering} days`, kind: "num" });
  }
  STRUCTURED_ENUM_FIELDS.forEach((f) => {
    const v = structured[f.key];
    if (v) rows.push({ key: f.key, label: f.label, value: ENUM_LABEL[v] || v, kind: "enum" });
  });
  const longNotes = [];
  if (structured.terpene_profile) longNotes.push({ label: "Terpene profile", value: structured.terpene_profile });
  if (structured.smoke_effect)    longNotes.push({ label: "Smoke / effect",  value: structured.smoke_effect });

  if (rows.length === 0 && longNotes.length === 0) return null;

  return (
    <div className="mt-3 pt-3 border-t border-[var(--border)]" data-testid={`review-structured-${reviewId}`}>
      <div className="label-eyebrow mb-2" style={{ color: "var(--text-muted)" }}>
        Structured cultivation report
      </div>
      {rows.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
          {rows.map((r) => (
            <div key={r.key} className="flex items-baseline justify-between gap-3 text-[12px]">
              <span className="font-mono uppercase tracking-[0.14em]" style={{ color: "var(--text-muted)" }}>
                {r.label}
              </span>
              <span className="font-mono" style={{ color: "var(--text)" }} data-testid={`structured-${r.key}-${reviewId}`}>
                {r.value}
              </span>
            </div>
          ))}
        </div>
      )}
      {longNotes.length > 0 && (
        <div className="mt-3 space-y-2">
          {longNotes.map((n) => (
            <div key={n.label}>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] mb-0.5"
                   style={{ color: "var(--text-muted)" }}>
                {n.label}
              </div>
              <p className="text-[13px]" style={{ color: "var(--text-2)" }}>{n.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GrowLogBackedPill({ growLog, reviewId }) {
  if (!growLog) return null;
  const level = growLog.verification_level || "self_reported";
  const levelLabel = ({
    self_reported: "Collector submission",
    verified_purchase: "Verified purchase",
    breeder_verified: "Breeder submission",
    community_supported: "Community-supported",
    legacy_archive: "Legacy archive",
  })[level] || "Collector submission";
  return (
    <Link
      to={`/grow-logs/${growLog.slug || growLog.id}`}
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em] border transition-colors hover:border-[var(--gold)]"
      style={{
        color: "var(--primary-text)",
        borderColor: "var(--primary-text)",
        background: "rgba(79,168,111,0.08)",
      }}
      title={`Backed by grow log · ${levelLabel}`}
      data-testid={`review-grow-log-backed-${reviewId}`}
    >
      <Sprout className="w-3 h-3" /> Grow-log backed · {levelLabel}
    </Link>
  );
}

export default function ReviewSection({ listingId, vendorId, vendorName, strainId, strainName, onReviewPosted }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [images, setImages] = useState([]);
  const [purchasedElsewhere, setPurchasedElsewhere] = useState(false);
  const [purchaseSource, setPurchaseSource] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // v3.99ah — Structured review state
  const [showStructured, setShowStructured] = useState(false);
  const [structured, setStructured] = useState(emptyStructured());
  const [myGrowLogs, setMyGrowLogs] = useState([]);
  const [growLogId, setGrowLogId] = useState("");
  const isVendorMode = !!vendorId && !listingId;

  const load = async () => {
    const url = listingId ? `/listings/${listingId}/reviews` : `/vendors/${vendorId}/reviews`;
    try {
      const { data } = await api.get(url);
      setReviews(data);
    } catch {
      setReviews([]);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId, vendorId]);

  const isLoggedIn = user && user !== false;
  const isOwnStorefront = isVendorMode && user && user.role === "vendor" && reviews.length === 0;
  const alreadyReviewed = isLoggedIn && reviews.some((r) => r.user_id === user?.id);

  // Strain reviews can come from buyers OR collectors who got the strain
  // elsewhere; both paths show the form. Breeder reviews: any logged-in user.
  const canSubmitForm = isVendorMode
    ? isLoggedIn && !alreadyReviewed && !isOwnStorefront
    : !!listingId && isLoggedIn && user?.role === "customer";

  // v3.99ah — Pull THIS collector's grow logs and narrow to ones that
  // match THIS strain. The grow-logs filter accepts a lineage id/slug;
  // when we only have the strain name we fall back to a client-side
  // case-insensitive name match so partial matches still surface.
  useEffect(() => {
    if (!canSubmitForm || isVendorMode || !user?.id) {
      setMyGrowLogs([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = { user_id: user.id, limit: 30 };
        if (strainId) params.strain_id = strainId;
        const { data } = await api.get("/grow-logs", { params });
        let logs = Array.isArray(data) ? data : [];
        if (!strainId && strainName) {
          const needle = strainName.trim().toLowerCase();
          logs = logs.filter((g) =>
            (g.strain_name || "").toLowerCase().includes(needle) ||
            needle.includes((g.strain_name || "").toLowerCase())
          );
        }
        if (!cancelled) setMyGrowLogs(logs);
      } catch {
        if (!cancelled) setMyGrowLogs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [canSubmitForm, isVendorMode, strainId, strainName, user?.id]);

  // Strip empty values from structured form for cleaner payload
  const buildStructuredPayload = () => {
    const out = {};
    STRUCTURED_SCORE_FIELDS.forEach(({ key }) => {
      if (structured[key] != null) out[key] = structured[key];
    });
    if (structured.flowering_time_days !== "" && structured.flowering_time_days != null) {
      const n = parseInt(structured.flowering_time_days, 10);
      if (!Number.isNaN(n) && n >= 20 && n <= 160) out.flowering_time_days = n;
    }
    STRUCTURED_ENUM_FIELDS.forEach(({ key }) => {
      if (structured[key]) out[key] = structured[key];
    });
    if (structured.terpene_profile && structured.terpene_profile.trim()) {
      out.terpene_profile = structured.terpene_profile.trim().slice(0, 240);
    }
    if (structured.smoke_effect && structured.smoke_effect.trim()) {
      out.smoke_effect = structured.smoke_effect.trim().slice(0, 240);
    }
    return out;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!isLoggedIn) { toast.error("Sign in to leave a review"); return; }
    setSubmitting(true);
    try {
      if (isVendorMode) {
        await api.post(`/vendors/${vendorId}/reviews`, { rating, body, images });
      } else {
        const payload = {
          listing_id: listingId,
          rating,
          body,
          images,
          purchased_elsewhere: purchasedElsewhere,
          purchase_source: purchasedElsewhere ? (purchaseSource || null) : null,
          ...buildStructuredPayload(),
        };
        if (growLogId) payload.grow_log_id = growLogId;
        await api.post("/reviews", payload);
      }
      setBody(""); setRating(5); setImages([]);
      setPurchasedElsewhere(false); setPurchaseSource("");
      setStructured(emptyStructured());
      setGrowLogId("");
      setShowStructured(false);
      toast.success("Review submitted — admin will publish it shortly");
      load();
      if (typeof onReviewPosted === "function") onReviewPosted();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const structuredFieldsFilled = useMemo(() => {
    return Object.keys(buildStructuredPayload()).length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structured]);

  return (
    <div data-testid="review-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="font-display text-2xl md:text-3xl tracking-tighter" style={{ color: "var(--text)" }}>
          {isVendorMode
            ? (vendorName ? `Reviews of ${vendorName}` : "Breeder reviews")
            : "Reviews"}
        </h3>
        <span className="text-sm" style={{ color: "var(--text-muted)" }}>
          {reviews.length} review{reviews.length === 1 ? "" : "s"}
        </span>
      </div>

      {/* Sign-in prompt for breeder reviews */}
      {isVendorMode && !isLoggedIn && (
        <div className="bg-[var(--surface-2)] border border-[var(--border)] p-4 mb-6 text-sm flex flex-wrap items-center gap-3"
             data-testid="review-signin-prompt">
          <span style={{ color: "var(--text-2)" }}>Want to leave a review?</span>
          <Link to="/login" className="underline" style={{ color: "var(--primary-text)" }} data-testid="review-signin-link">Sign in</Link>
          <span style={{ color: "var(--text-muted)" }}>or</span>
          <Link to="/register" className="underline" style={{ color: "var(--gold)" }} data-testid="review-join-link">Join as a collector</Link>
        </div>
      )}

      {isLoggedIn && alreadyReviewed && (
        <div className="bg-[var(--surface-2)] border border-[var(--border)] p-4 mb-6 text-sm"
             style={{ color: "var(--text-2)" }} data-testid="review-already">
          You&apos;ve already reviewed this {isVendorMode ? "breeder" : "strain"}. Thanks for your feedback.
        </div>
      )}

      {canSubmitForm && (
        <form onSubmit={submit}
              className="bg-[var(--surface-2)] border border-[var(--border)] p-4 mb-6"
              data-testid="review-form">
          <div className="label-eyebrow mb-2">Leave a review</div>
          <div className="mb-3">
            <StarRow value={rating} size={22} onClick={setRating} />
          </div>
          <textarea value={body} onChange={(e) => setBody(e.target.value)}
                    placeholder={isVendorMode
                      ? "Share your experience with this breeder — quality, communication, drop reliability…"
                      : "Share your thoughts on this strain — taste, terps, yield, what you'd grow it for…"}
                    className="input-field"
                    rows={3} maxLength={2000} data-testid="review-body"/>

          {/* v3.65: image upload (up to 4) */}
          <div className="mt-3">
            <div className="label-eyebrow mb-1.5" style={{color:"var(--text-muted)"}}>
              <ImageIcon className="inline w-3 h-3 mr-1"/> Photos (optional · up to 4)
            </div>
            <ImageUploader value={images} onChange={setImages} multiple maxItems={4} label="" testIdPrefix="review-photos"/>
          </div>

          {/* v3.99ah — Phase 1 P1: Structured cultivation report (strain reviews only) */}
          {!isVendorMode && (
            <div className="mt-4 pt-4 border-t border-[var(--border)]">
              <button
                type="button"
                onClick={() => setShowStructured((v) => !v)}
                className="w-full flex items-center justify-between text-left"
                data-testid="review-structured-toggle"
              >
                <div>
                  <div className="label-eyebrow" style={{ color: "var(--gold)" }}>
                    Structured cultivation report
                  </div>
                  <div className="text-[12px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                    Optional · 14 fields · helps build the Forge Index
                    {structuredFieldsFilled > 0 && (
                      <span className="ml-2 font-mono" style={{ color: "var(--primary-text)" }}>
                        · {structuredFieldsFilled} field{structuredFieldsFilled === 1 ? "" : "s"} filled
                      </span>
                    )}
                  </div>
                </div>
                {showStructured
                  ? <ChevronUp className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
                  : <ChevronDown className="w-4 h-4" style={{ color: "var(--text-muted)" }} />}
              </button>

              {showStructured && (
                <div className="mt-4 space-y-4" data-testid="review-structured-panel">
                  {/* Score fields */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {STRUCTURED_SCORE_FIELDS.map((f) => (
                      <div key={f.key} className="flex items-center justify-between gap-3">
                        <label className="text-[12px] font-mono uppercase tracking-[0.14em]"
                               style={{ color: "var(--text-muted)" }}>
                          {f.label}
                        </label>
                        <ScoreStepper
                          value={structured[f.key]}
                          onChange={(v) => setStructured((s) => ({ ...s, [f.key]: v }))}
                          testId={`structured-${f.key}`}
                        />
                      </div>
                    ))}
                  </div>

                  {/* Flowering time */}
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-[12px] font-mono uppercase tracking-[0.14em]"
                           style={{ color: "var(--text-muted)" }}>
                      Flowering time (days)
                    </label>
                    <input
                      type="number"
                      min={20}
                      max={160}
                      value={structured.flowering_time_days}
                      onChange={(e) => setStructured((s) => ({ ...s, flowering_time_days: e.target.value }))}
                      className="input-field w-28 text-right font-mono"
                      placeholder="—"
                      data-testid="structured-flowering_time_days"
                    />
                  </div>

                  {/* Enum dropdowns */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {STRUCTURED_ENUM_FIELDS.map((f) => (
                      <div key={f.key} className="flex items-center justify-between gap-3">
                        <label className="text-[12px] font-mono uppercase tracking-[0.14em]"
                               style={{ color: "var(--text-muted)" }}>
                          {f.label}
                        </label>
                        <select
                          value={structured[f.key]}
                          onChange={(e) => setStructured((s) => ({ ...s, [f.key]: e.target.value }))}
                          className="input-field font-mono text-[12px]"
                          data-testid={`structured-${f.key}`}
                        >
                          <option value="">—</option>
                          {f.options.map((o) => (
                            <option key={o} value={o}>{ENUM_LABEL[o] || o}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  {/* Long notes */}
                  <div>
                    <label className="label-eyebrow mb-1 block" style={{ color: "var(--text-muted)" }}>
                      Terpene profile
                    </label>
                    <input
                      type="text"
                      maxLength={240}
                      value={structured.terpene_profile}
                      onChange={(e) => setStructured((s) => ({ ...s, terpene_profile: e.target.value }))}
                      className="input-field w-full"
                      placeholder="Gas, citrus rind, faint diesel on the back end…"
                      data-testid="structured-terpene_profile"
                    />
                  </div>
                  <div>
                    <label className="label-eyebrow mb-1 block" style={{ color: "var(--text-muted)" }}>
                      Smoke / effect
                    </label>
                    <input
                      type="text"
                      maxLength={240}
                      value={structured.smoke_effect}
                      onChange={(e) => setStructured((s) => ({ ...s, smoke_effect: e.target.value }))}
                      className="input-field w-full"
                      placeholder="Heavy body, long tail, couch lock after the second bowl…"
                      data-testid="structured-smoke_effect"
                    />
                  </div>

                  {/* Link to grow log */}
                  {myGrowLogs.length > 0 && (
                    <div className="pt-3 border-t border-[var(--border)]">
                      <label className="label-eyebrow mb-1 block flex items-center gap-1.5"
                             style={{ color: "var(--gold)" }}>
                        <Link2 className="w-3 h-3" />
                        Link to one of your grow logs (boosts review weight)
                      </label>
                      <select
                        value={growLogId}
                        onChange={(e) => setGrowLogId(e.target.value)}
                        className="input-field w-full font-mono text-[12px]"
                        data-testid="review-grow-log-select"
                      >
                        <option value="">— No linked grow log —</option>
                        {myGrowLogs.map((g) => (
                          <option key={g.id} value={g.id}>
                            {(g.title || g.strain_name || "Untitled grow")} · {g.log_type === "archived" ? "archived" : (g.status || "live")}
                          </option>
                        ))}
                      </select>
                      <p className="text-[11px] mt-1.5" style={{ color: "var(--text-muted)" }}>
                        Reviews backed by real grow logs feed the Forge Index at higher trust weight.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* v3.65: "ordered elsewhere" toggle on strain reviews only */}
          {!isVendorMode && (
            <div className="mt-3 pt-3 border-t border-[var(--border)]">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={purchasedElsewhere}
                       onChange={(e) => setPurchasedElsewhere(e.target.checked)}
                       className="mt-1 accent-[var(--gold)]"
                       data-testid="review-purchased-elsewhere"/>
                <span className="text-sm" style={{color:"var(--text-2)"}}>
                  <strong style={{color:"var(--text)"}}>I ordered this elsewhere</strong>
                  <span className="block text-xs mt-0.5" style={{color:"var(--text-muted)"}}>
                    Check this if you got the strain from the breeder&apos;s own site or another marketplace before CannaForge. Your review will be flagged "Verified elsewhere".
                  </span>
                </span>
              </label>
              {purchasedElsewhere && (
                <input value={purchaseSource}
                       onChange={(e) => setPurchaseSource(e.target.value)}
                       className="input-field mt-2 w-full"
                       placeholder="Where did you order it? (Breeder's site, IG, another marketplace…)"
                       maxLength={80}
                       data-testid="review-purchase-source"/>
              )}
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-[var(--border)] flex items-center justify-between flex-wrap gap-2">
            <button type="submit" disabled={submitting} className="btn-primary" data-testid="review-submit">
              {submitting ? "Submitting…" : "Submit review"}
            </button>
            <span className="text-xs" style={{ color: "var(--text-muted)" }}>
              Reviews are admin-approved before going live. Approved reviews earn you collector points.
            </span>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {reviews.length === 0 ? (
          <div className="bg-[var(--surface)] border border-dashed border-[var(--border)] p-6"
               data-testid="reviews-empty">
            <div className="label-eyebrow mb-2" style={{ color: "var(--text-muted)" }}>
              No structured reviews yet
            </div>
            <p className="text-sm leading-relaxed" style={{ color: "var(--text-2)" }}>
              {isVendorMode
                ? "This breeder hasn't received structured reviews yet. Verified purchasers can document germination, vigor, flowering time, and smoke notes — that documentation feeds the Forge Index and helps the next collector decide."
                : "No structured reviews on this cultivar yet. Verified purchasers who document their grow can attach a 14-field structured report linked to their grow log — building the cultivar's verified evidence record."}
            </p>
          </div>
        ) : (
          <ReviewList reviews={reviews} />
        )}
      </div>
    </div>
  );
}

// v3.99bj — Best-3-first review list with Show-more toggle.
// "Best" sort:
//   1. Verified-purchase reviews (most trustworthy)
//   2. Grow-log-backed reviews
//   3. Higher star rating
//   4. Most recent
// Falls back gracefully when none of those signals exist.
function ReviewList({ reviews }) {
  const INITIAL = 3;
  const [expanded, setExpanded] = React.useState(false);

  const ranked = React.useMemo(() => {
    const scoreOf = (r) => {
      let s = 0;
      if (r.verified_purchase) s += 100;
      if (r.grow_log) s += 60;
      if (r.verified_elsewhere) s += 25;
      s += (r.rating || 0) * 12;
      // Tie-break: newer beats older.
      s += new Date(r.created_at || 0).getTime() / 1e12;
      return s;
    };
    return reviews.slice().sort((a, b) => scoreOf(b) - scoreOf(a));
  }, [reviews]);

  const visible = expanded ? ranked : ranked.slice(0, INITIAL);
  const hidden  = Math.max(0, ranked.length - INITIAL);

  return (
    <>
      {visible.map((r) => <ReviewCard key={r.id} r={r} />)}
      {hidden > 0 && !expanded && (
        <div className="flex justify-center pt-2">
          <button onClick={() => setExpanded(true)}
                  data-testid="reviews-show-more"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-[11px] font-mono uppercase tracking-[0.2em] border border-[var(--border-strong)] text-[var(--text)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors">
            Show {hidden} more review{hidden === 1 ? "" : "s"}
          </button>
        </div>
      )}
      {expanded && ranked.length > INITIAL && (
        <div className="flex justify-center pt-2">
          <button onClick={() => setExpanded(false)}
                  data-testid="reviews-show-less"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-[11px] font-mono uppercase tracking-[0.2em] border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] transition-colors">
            Show less
          </button>
        </div>
      )}
    </>
  );
}

function ReviewCard({ r }) {
  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] p-4"
         data-testid={`review-${r.id}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <StarRow value={r.rating} />
          <span className="text-sm font-semibold" style={{ color: "var(--text)" }}>{r.user_name}</span>
          {r.verified_purchase && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em] border"
                  style={{color:"var(--primary-text)", borderColor:"var(--primary-text)", background:"rgba(79,168,111,0.08)"}}
                  title="Confirmed CannaForge order"
                  data-testid={`verified-purchase-${r.id}`}>
              <BadgeCheck className="w-3 h-3" /> Verified purchase
            </span>
          )}
          {r.verified_elsewhere && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.18em] border"
                  style={{color:"var(--gold)", borderColor:"var(--gold)", background:"rgba(212,175,55,0.06)"}}
                  title={r.purchase_source ? `Bought elsewhere: ${r.purchase_source}` : "Bought outside CannaForge"}
                  data-testid={`verified-elsewhere-${r.id}`}>
              <MapPin className="w-3 h-3" /> Verified elsewhere
            </span>
          )}
          <GrowLogBackedPill growLog={r.grow_log} reviewId={r.id} />
        </div>
        <span className="text-xs" style={{ color: "var(--text-muted)" }}>
          {new Date(r.created_at).toLocaleDateString()}
        </span>
      </div>
      {r.body && <p className="text-sm mt-2" style={{ color: "var(--text-2)" }}>{r.body}</p>}
      {(r.images || []).length > 0 && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {r.images.map((src, i) => (
            <a key={i} href={src} target="_blank" rel="noreferrer"
               className="block border border-[var(--border)] hover:border-[var(--gold)] transition-colors">
              <img src={src} alt={`Review photo ${i+1}`} className="w-20 h-20 object-cover"/>
            </a>
          ))}
        </div>
      )}
      <StructuredReviewDisplay structured={r.structured} reviewId={r.id} />
    </div>
  );
}
